'use client'

/**
 * Layout Component
 * Global layout wrapper for all pages
 *
 * Features:
 * - Header with navigation
 * - Sidebar for admin users
 * - User menu with profile link and logout
 * - Main content area
 * - Responsive design
 */

import React, { useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import Sidebar from './Sidebar'
import UpdateBanner from './UpdateBanner'
import { useAuth } from '../context/AuthContext'

export default function Layout({ children, user, onLogout }) {
  const { isImpersonating, stopImpersonation } = useAuth()
  const router = useRouter()
  const [menuOpen, setMenuOpen] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const isAdmin = user?.tier === 'admin' && !isImpersonating

  const handleLogout = async () => {
    setMenuOpen(false)
    setSidebarOpen(false)
    await onLogout()
    router.push('/login')
  }

  // Public pages (no header needed)
  const isPublicPage = ['/login', '/register', '/'].includes(router.pathname)

  if (isPublicPage) {
    return <>{children}</>
  }

  // Determine active section from query
  const section = router.query.section || 'dashboard'

  const handleNavigate = (newSection) => {
    if (newSection === 'dashboard') {
      router.push('/dashboard')
    } else {
      router.push(`/dashboard?section=${newSection}`)
    }
  }

  const handleStopImpersonation = async () => {
    try {
      await stopImpersonation()
      router.push('/dashboard?section=admin')
    } catch (err) {
      // Error handled by context
    }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div style={{
          position: 'sticky',
          top: 0,
          background: 'linear-gradient(90deg, rgba(255,179,0,0.15), rgba(255,179,0,0.08))',
          borderBottom: '1px solid rgba(255,179,0,0.3)',
          padding: '8px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          zIndex: 100,
          backdropFilter: 'blur(12px)',
        }}>
          <span style={{
            fontFamily: 'var(--font-terminal)',
            fontSize: '0.6875rem',
            color: 'var(--sc-amber-bright)',
            fontWeight: 600,
            letterSpacing: '0.05em',
          }}>
            ⚠ IMPERSONATING: {user?.username} ({user?.tier})
          </span>
          <button
            onClick={handleStopImpersonation}
            style={{
              background: 'rgba(255,179,0,0.2)',
              border: '1px solid rgba(255,179,0,0.4)',
              color: 'var(--sc-amber-bright)',
              padding: '3px 12px',
              borderRadius: 'var(--radius-base)',
              fontFamily: 'var(--font-terminal)',
              fontSize: '0.625rem',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            $ stop
          </button>
        </div>
      )}

      {/* Header — offset top when impersonation bar is visible */}
      <header className="sc-nav" style={isImpersonating ? { top: '33px' } : undefined}>
        <div className="sc-nav-inner">
          {/* Hamburger button (mobile, admin only) */}
          {isAdmin && (
            <button
              className="sc-topbar-hamburger"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label="Toggle sidebar"
            >
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '1.125rem' }}>
                {sidebarOpen ? '\u2715' : '\u2261'}
              </span>
            </button>
          )}

          {/* Logo/Brand */}
          <Link href={user ? '/dashboard' : '/'} className="sc-nav-brand" style={{ textDecoration: 'none' }}>
            <div className="sc-nav-brand-icon" style={{ fontSize: '0.5rem', letterSpacing: '-0.02em' }}>CCM</div>
            <span style={{ fontSize: '0.8125rem', fontFamily: 'var(--font-display)', fontWeight: 600, color: 'var(--sc-text-primary)', letterSpacing: '0.03em' }}>CCMakers Code</span>
          </Link>

          {/* Spacer */}
          <div style={{ flex: 1 }} />

          {/* User Menu */}
          {user ? (
            <div style={{ position: 'relative' }}>
              {/* User pill button */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="sc-nav-user"
              >
                <div className="sc-nav-user-avatar">
                  {user.username.charAt(0).toUpperCase()}
                </div>
                <div className="sc-nav-user-info">
                  <div className="sc-nav-user-name">{user.username}</div>
                  <div className="sc-nav-user-tier">{user.tier}</div>
                </div>
              </button>

              {/* Dropdown Menu */}
              {menuOpen && (
                <div className="sc-dropdown">
                  {/* User Info */}
                  <div className="sc-dropdown-header">
                    <p style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--sc-text-primary)', margin: 0 }}>{user.username}</p>
                    <p style={{ fontSize: '0.6875rem', color: 'var(--sc-text-muted)', margin: 0 }}>{user.email}</p>
                  </div>

                  {/* Menu Items */}
                  <div style={{ padding: '4px 0' }}>
                    <Link
                      href="/dashboard"
                      onClick={() => setMenuOpen(false)}
                      className="sc-dropdown-item"
                    >
                      ~/dashboard
                    </Link>

                    <hr className="sc-dropdown-divider" />

                    <button
                      onClick={handleLogout}
                      className="sc-dropdown-item sc-dropdown-item--danger"
                    >
                      $ logout --force
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '8px' }}>
              <Link href="/login" className="sc-nav-link">
                &gt; login
              </Link>
              <Link href="/register" className="btn-cmd" style={{ padding: '6px 14px', fontSize: '0.75rem' }}>
                $ register
              </Link>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      {isAdmin ? (
        <div className="sc-layout-body">
          <Sidebar
            user={user}
            activeSection={section}
            onNavigate={handleNavigate}
            isOpen={sidebarOpen}
            onToggle={() => setSidebarOpen(!sidebarOpen)}
            onLogout={handleLogout}
          />
          <div className="sc-layout-content">
            <main style={{ flex: 1 }}>{children}</main>
          </div>
        </div>
      ) : (
        <main style={{ flex: 1, width: '100%', maxWidth: '640px', margin: '0 auto', padding: '1rem' }}>
          {children}
        </main>
      )}

      {/* Footer */}
      <footer className="sc-footer">
        <div className="sc-footer-inner">
          <p style={{ margin: 0 }}>// CCMakers Code v1.0 | {new Date().getFullYear()} | all systems nominal</p>
        </div>
      </footer>

      {/* Toast de atualização — aparece em todas as páginas autenticadas */}
      <UpdateBanner />
    </div>
  )
}
